import React, { useState, useRef } from 'react';
import { 
  Container, 
  Grid, 
  Box,
  Typography, 
  List, 
  ListItem, 
  ListItemText,
  ListItemButton,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccreditationCard from '../components/AccreditationCard';
import AccreditationsInfo from '../components/AccreditationsInfo';
import accreditationsData from '../data/accreditations.json';
import TitlePage from '../components/TitlePage';
import headerImage from '../assets/images/about/header.jpg';

const PageContainer = styled(Box)(({ theme }) => ({
  paddingBottom: '2rem',
}));

const SidebarContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  padding: '1.5rem'
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  padding: '2rem',
  minHeight: '600px',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#1A365D',
  marginBottom: '2rem'
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: '8px',
  '&.Mui-selected': {
    backgroundColor: '#F1F5F9',
    '&:hover': {
      backgroundColor: '#F1F5F9'
    }
  }
}));

const SubtitleTypography = styled(Typography)(({ theme }) => ({
  color: '#1A365D',
  fontWeight: 600,
  marginBottom: '0.5rem'
}));

const Accreditations = () => {
  const [selectedCompany, setSelectedCompany] = useState('l2mi');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSector, setSelectedSector] = useState('all');
  const [selectedAccreditation, setSelectedAccreditation] = useState('all');
  const listRef = useRef(null);

  const companies = [
    { id: 'l2mi', name: 'L2MI' },
    { id: 'airmetrologie', name: 'AIR MÉTROLOGIE' }
  ];

  const types = [
    { id: 'all', name: 'Toutes les accréditations' },
    { id: 'COFRAC', name: 'COFRAC' },
    { id: 'SEMAC', name: 'SEMAC' },
    { id: 'TUNAC', name: 'TUNAC' }
  ];

  const filteredAccreditations = accreditationsData[selectedCompany].accreditations.filter(
    accreditation => selectedType === 'all' || accreditation.type === selectedType
  );

  const handleSectorChange = (event) => {
    setSelectedSector(event.target.value);
  };

  const handleAccreditationChange = (event) => {
    setSelectedAccreditation(event.target.value);
  };

  const scrollToList = () => {
    const yOffset = -100;
    const element = listRef.current;
    if (element) {
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <PageContainer>
      <TitlePage 
        title="Nos Accréditations" 
        image={headerImage}
      />

      <AccreditationsInfo onScrollToList={scrollToList} />

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 8, lg: 12 }, mt: 6 }} ref={listRef}>
        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <SidebarContainer>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#1A365D',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  mb: 3 
                }}
              >
                Filtres
              </Typography>
              
              <SubtitleTypography>
                Entreprise
              </SubtitleTypography>
              <List disablePadding>
                {companies.map((company) => (
                  <ListItem key={company.id} disablePadding>
                    <StyledListItemButton
                      selected={selectedCompany === company.id}
                      onClick={() => setSelectedCompany(company.id)}
                    >
                      <ListItemText 
                        primary={company.name}
                        primaryTypographyProps={{
                          sx: { 
                            color: '#475569',
                            fontWeight: selectedCompany === company.id ? 600 : 400
                          }
                        }}
                      />
                    </StyledListItemButton>
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <SubtitleTypography>
                Type d'accréditation
              </SubtitleTypography>
              <List disablePadding>
                {types.map((type) => (
                  <ListItem key={type.id} disablePadding>
                    <StyledListItemButton
                      selected={selectedType === type.id}
                      onClick={() => setSelectedType(type.id)}
                    >
                      <ListItemText 
                        primary={type.name}
                        primaryTypographyProps={{
                          sx: { 
                            color: '#475569',
                            fontWeight: selectedType === type.id ? 600 : 400
                          }
                        }}
                      />
                    </StyledListItemButton>
                  </ListItem>
                ))}
              </List>
            </SidebarContainer>
          </Grid>

          {/* Contenu principal */}
          <Grid item xs={12} md={9}>
            <ContentContainer>
              <Title>
                {accreditationsData[selectedCompany].name}
              </Title>

              <Grid container spacing={3}>
                {filteredAccreditations.map((accreditation) => (
                  <Grid item xs={12} sm={6} md={4} key={accreditation.id}>
                    <AccreditationCard 
                      accreditation={accreditation}
                      company={accreditationsData[selectedCompany].name}
                    />
                  </Grid>
                ))}
              </Grid>
            </ContentContainer>
          </Grid>
        </Grid>
      </Container>
    </PageContainer>
  );
};

export default Accreditations;
